import { ReactElement, useEffect } from 'react'

import { ErrorMessage, Field, Form, Formik } from 'formik'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { boolean, object, string } from 'yup'

import { useAppConfig } from 'appConfig'
import { FormikCheckbox, FormikInput } from 'components/formik'
import { ToolTip } from 'components/ToolTip'
import { MIN_PW_SIZE } from 'const/auth'
import { SegmentEventName } from 'const/monitoring/segment'
import { signUp } from 'features/Auth/authService'
import { useAnalytics } from 'features/Monitoring/Segment/useAnalytics'
import { SignupData } from 'helpers/params'
import { TransObj } from 'helpers/translation'
import { getPath } from 'routing/pagePaths'
import { OrganizationInvitation } from 'types'

import { AuthError, SubmitButton } from '../Shared'
import * as authStyles from '../styles.scss'

interface Props {
    invitation?: OrganizationInvitation
    token?: string
}

const CreateAccountForm = ({ invitation, token }: Props): ReactElement => {
    const { t } = useTranslation('account')
    const { productTitle } = useAppConfig()

    const { trackEvent, trackCtaClick } = useAnalytics()

    useEffect(() => {
        trackEvent(SegmentEventName.SIGN_UP_STARTED, {
            text: 'Sign Up - Sign Up Started',
        })
    }, [trackEvent])

    const validationSchema = object().shape({
        name: string().required().label(t('account:name', 'Name')),
        email: string()
            .email()
            .trim()
            .lowercase()
            .required()
            .label(t('account:email', 'Email')),
        password: string()
            .min(MIN_PW_SIZE)
            .required()
            .label(t('account:password', 'Password')),
        terms_of_service: boolean().oneOf(
            [true],
            t(
                'account:CreateAccountForm.terms_of_service.errorMsg',
                'Please agree to our terms of service'
            )
        ),
    })

    const renderTitle = (): ReactElement => {
        if (invitation) {
            return (
                <div className={authStyles.authFormTitleBlock}>
                    <h1>
                        {t(
                            'account:CreateAccountForm.invitedMember.title',
                            'Welcome to {{ productTitle }}!',
                            { productTitle }
                        )}
                    </h1>
                    <div className={authStyles.authFormSubtitle}>
                        <Trans i18nKey="account:CreateAccountForm.invitedMember.subTitle">
                            You&apos;ve been invited to join{' '}
                            <strong>
                                {
                                    {
                                        orgName: invitation.organization.name,
                                    } as TransObj
                                }
                            </strong>{' '}
                            by
                            <br />
                            {
                                {
                                    inviterEmail: invitation.inviter.email,
                                } as TransObj
                            }
                        </Trans>
                    </div>
                </div>
            )
        }
        return (
            <div className={authStyles.authFormTitleBlock}>
                <h1>{t('account:CreateAccountForm.title', 'Sign up')}</h1>
                <div className={authStyles.authFormSubtitle}>
                    <Trans i18nKey="account:CreateAccountForm.subTitle">
                        Create your account, then join or create
                        <br /> an organization
                    </Trans>
                </div>
            </div>
        )
    }

    return (
        <>
            {renderTitle()}

            <Formik
                initialValues={{
                    name: '',
                    email: invitation?.user_email || '',
                    password: '',
                    terms_of_service: false,
                }}
                onSubmit={(values) => {
                    trackCtaClick({
                        destination: 'Sign in - Login Page',
                        text: 'Sign Up',
                        type: 'button',
                        location: 'Sign Up Page',
                    })
                    signUp({
                        ...validationSchema.cast(values),
                        token,
                    } as SignupData)
                }}
                validationSchema={validationSchema}
            >
                {() => (
                    <Form>
                        <div style={{ marginBottom: 8 }}>
                            <Field
                                component={FormikInput}
                                placeholder={t('account:name', 'Name')}
                                name="name"
                                size="large"
                            />
                            <ErrorMessage
                                name="name"
                                component="div"
                                className="fg-control-text is-error"
                            />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <Field
                                component={FormikInput}
                                placeholder={t('account:email', 'Email')}
                                name="email"
                                size="large"
                                disabled={!!invitation}
                            />
                            <ErrorMessage
                                name="email"
                                component="div"
                                className="fg-control-text is-error"
                            />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <ToolTip
                                placement="right"
                                title={t(
                                    'account:PasswordField.securityTooltip',
                                    'We check passwords against common and compromised passwords to keep your account secure.'
                                )}
                            >
                                <Field
                                    component={FormikInput}
                                    placeholder={t(
                                        'account:password',
                                        'Password'
                                    )}
                                    name="password"
                                    type="password"
                                    size="large"
                                />
                            </ToolTip>
                            <ErrorMessage
                                name="password"
                                component="div"
                                className="fg-control-text is-error"
                            />
                        </div>
                        <div style={{ marginTop: 16 }}>
                            <Field
                                component={FormikCheckbox}
                                name="terms_of_service"
                            >
                                <Trans i18nKey="account:CreateAccountForm.termsOfServiceLink">
                                    I agree to {{ productTitle } as TransObj}
                                    &apos;s{' '}
                                    <a
                                        href={TERMS_OF_SERVICE}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        Terms of Service
                                    </a>
                                    .
                                </Trans>
                            </Field>
                            <ErrorMessage
                                name="terms_of_service"
                                component="div"
                                className="fg-control-text is-error"
                            />
                        </div>
                        <SubmitButton>
                            {t(
                                'account:CreateAccountForm.submitBtnText',
                                'Sign Up'
                            )}
                        </SubmitButton>
                    </Form>
                )}
            </Formik>

            <AuthError />

            <footer className={authStyles.authFormFooter}>
                <Trans i18nKey="account:CreateAccountForm.logInLink">
                    Already have a {{ productTitle } as TransObj} account?{' '}
                    <Link to={getPath('authPage')}>
                        <span>Log In</span>
                    </Link>
                </Trans>
            </footer>
        </>
    )
}

export default CreateAccountForm
